import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { 
  Database, ExternalLink, Calendar, MapPin, User, 
  FileText, ChevronDown, ChevronUp, CheckCircle2, 
  AlertCircle, Image, Loader2
} from 'lucide-react';
import { getCanonicalCase } from '@/lib/api';
import { motion, AnimatePresence } from 'framer-motion';

const SOURCE_CONFIG = {
  namus: { 
    name: 'NamUs', 
    color: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300',
    urlPrefix: 'https://www.namus.gov/MissingPersons/Case#/',
    description: 'National Missing and Unidentified Persons System'
  },
  charley: { 
    name: 'Charley Project', 
    color: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
    urlPrefix: 'https://charleyproject.org/case/',
    description: 'Independent missing persons database'
  },
  ncmec: { 
    name: 'NCMEC', 
    color: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
    urlPrefix: 'https://www.missingkids.org/poster/',
    description: 'National Center for Missing & Exploited Children'
  },
  namus_unidentified: {
    name: 'NamUs Unidentified',
    color: 'bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300',
    urlPrefix: 'https://www.namus.gov/UnidentifiedPersons/Case#/',
    description: 'Unidentified persons database'
  },
  doenetwork: {
    name: 'Doe Network',
    color: 'bg-pink-100 text-pink-700 dark:bg-pink-950 dark:text-pink-300',
    urlPrefix: 'https://www.doenetwork.org/',
    description: 'Volunteer organization for unidentified persons'
  }
};

const SourceCard = ({ source, isExpanded, onToggle }) => {
  const config = SOURCE_CONFIG[source.source] || {
    name: source.source?.toUpperCase() || 'Unknown',
    color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    description: 'External data source'
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Build external URL
  const getExternalUrl = () => {
    if (source.source_url) return source.source_url;
    if (source.external_id && config.urlPrefix) {
      const id = source.external_id.replace(/^(namus_|charley_|fbi_)/, '');
      return config.urlPrefix + id;
    }
    return null;
  };

  const externalUrl = getExternalUrl();
  
  // Get unique fields from this source
  const hasPhoto = source.photo_url && source.photo_url.trim() !== '';
  const hasDescription = source.description && source.description.trim() !== '';
  const hasDistinguishingFeatures = source.distinguishing_features && source.distinguishing_features.trim() !== '';

  // Get proxied image URL for NamUs images (to bypass CORS)
  const getProxiedPhotoUrl = () => {
    const url = source.photo_url;
    if (!url) return null;
    const API = process.env.REACT_APP_BACKEND_URL;
    if (url.includes('namus.gov') && url.includes('/Images/')) {
      const match = url.match(/CaseSets\/NamUs\/(MissingPersons|UnclaimedPersons|UnidentifiedPersons)\/Cases\/(\d+)\/Images\/(\d+)/);
      if (match) {
        const [, caseType, caseId, imageId] = match;
        return `${API}/api/external/namus-image/${caseId}/${imageId}?size=Thumbnail&case_type=${caseType}`;
      }
      const fallbackMatch = url.match(/Cases\/(\d+)\/Images\/(\d+)/);
      if (fallbackMatch) {
        const [, caseId, imageId] = fallbackMatch;
        return `${API}/api/external/namus-image/${caseId}/${imageId}?size=Thumbnail`;
      }
    }
    return url;
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader 
        className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors py-4"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Badge className={`${config.color} border-0`}>
              <Database className="w-3 h-3 mr-1" />
              {config.name}
            </Badge>
            <span className="text-sm text-muted-foreground">
              {config.description}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {hasPhoto && (
              <Badge variant="outline" className="text-xs">
                <Image className="w-3 h-3 mr-1" />
                Photo
              </Badge>
            )}
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-5 h-5 text-muted-foreground" />
            )}
          </div>
        </div>
      </CardHeader>
      
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Separator />
            <CardContent className="pt-4 space-y-4">
              {/* Photo from this source */}
              {hasPhoto && (
                <div className="flex gap-4">
                  <div className="w-24 h-32 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-800 flex-shrink-0">
                    <img 
                      src={getProxiedPhotoUrl()} 
                      alt={`${source.name} from ${config.name}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center text-slate-400"><svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                      }}
                    />
                  </div>
                  <div className="flex-1 text-sm text-muted-foreground">
                    <p className="font-medium text-foreground mb-1">Photo from {config.name}</p>
                    <p className="text-xs break-all opacity-70">{source.photo_url}</p>
                  </div>
                </div>
              )}
              
              {/* Data fields grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <User className="w-4 h-4 mt-0.5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Name</p>
                      <p className="text-muted-foreground">{source.name || 'Not provided'}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 mt-0.5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Location</p>
                      <p className="text-muted-foreground">
                        {source.last_seen_location || source.city || 'Not provided'}
                        {source.state && `, ${source.state}`}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-2">
                    <Calendar className="w-4 h-4 mt-0.5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Date</p>
                      <p className="text-muted-foreground">
                        {formatDate(source.last_seen_date || source.missing_since)}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div>
                    <p className="font-medium">Physical Description</p>
                    <p className="text-muted-foreground">
                      {source.age && `${source.age} years old`}
                      {source.gender && ` • ${source.gender}`}
                      {source.race && ` • ${source.race}`}
                    </p>
                  </div>
                  
                  {(source.height || source.weight) && (
                    <div>
                      <p className="font-medium">Build</p>
                      <p className="text-muted-foreground">
                        {source.height && `Height: ${source.height}`}
                        {source.height && source.weight && ' • '}
                        {source.weight && `Weight: ${source.weight}`}
                      </p>
                    </div>
                  )}
                  
                  {(source.hair_color || source.eye_color) && (
                    <div>
                      <p className="font-medium">Features</p>
                      <p className="text-muted-foreground">
                        {source.hair_color && `Hair: ${source.hair_color}`}
                        {source.hair_color && source.eye_color && ' • '}
                        {source.eye_color && `Eyes: ${source.eye_color}`}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Distinguishing Features */}
              {hasDistinguishingFeatures && (
                <div className="pt-2">
                  <p className="font-medium text-sm mb-1">Distinguishing Features</p>
                  <p className="text-sm text-muted-foreground">{source.distinguishing_features}</p>
                </div>
              )}
              
              {/* Description */}
              {hasDescription && (
                <div className="pt-2">
                  <p className="font-medium text-sm mb-1">Description from {config.name}</p>
                  <p className="text-sm text-muted-foreground line-clamp-4">{source.description}</p>
                </div>
              )}
              
              {/* External Link */}
              {externalUrl && (
                <div className="pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full"
                    onClick={() => window.open(externalUrl, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    View on {config.name}
                  </Button>
                </div>
              )}
            </CardContent>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
};

export default function SourcesTab({ caseId, isCanonical }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sources, setSources] = useState([]);
  const [canonical, setCanonical] = useState(null);
  const [expandedSources, setExpandedSources] = useState({});

  useEffect(() => {
    if (!isCanonical || !caseId) {
      setLoading(false);
      return;
    }

    const fetchSources = async () => {
      try {
        setLoading(true);
        const data = await getCanonicalCase(caseId);
        setCanonical(data.canonical);
        setSources(data.sources || []);
        
        // Auto-expand first source
        if (data.sources?.length > 0) {
          setExpandedSources({ [data.sources[0].id]: true });
        }
      } catch (err) {
        console.error('Failed to fetch sources:', err);
        setError('Failed to load source data');
      } finally {
        setLoading(false);
      }
    };

    fetchSources();
  }, [caseId, isCanonical]);

  const toggleSource = (sourceId) => {
    setExpandedSources(prev => ({
      ...prev,
      [sourceId]: !prev[sourceId]
    }));
  };

  if (!isCanonical) {
    return null;
  }

  if (loading) {
    return (
      <Card className="p-8">
        <div className="flex items-center justify-center gap-3 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading source data...</span>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-8">
        <div className="flex items-center justify-center gap-3 text-red-500">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      </Card>
    );
  }

  if (sources.length === 0) {
    return (
      <Card className="p-8">
        <div className="text-center text-muted-foreground">
          <Database className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>No source data available</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Database className="w-5 h-5" />
            Data Sources
          </h3>
          <p className="text-sm text-muted-foreground">
            This case combines data from {sources.length} official {sources.length === 1 ? 'source' : 'sources'}
          </p>
        </div>
        <Badge variant="outline" className="text-sm">
          {sources.length} {sources.length === 1 ? 'Source' : 'Sources'}
        </Badge>
      </div>
      
      {/* Explanation card */}
      <Card className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
        <CardContent className="py-3 px-4">
          <p className="text-sm text-blue-700 dark:text-blue-300 flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              We combine data from multiple official sources to provide the most complete and accurate information. 
              Each source may have unique details not found elsewhere.
            </span>
          </p>
        </CardContent>
      </Card>
      
      {/* Source cards */}
      <div className="space-y-3">
        {sources.map((source) => (
          <SourceCard
            key={source.id}
            source={source}
            isExpanded={expandedSources[source.id] || false}
            onToggle={() => toggleSource(source.id)}
          />
        ))}
      </div>
    </div>
  );
}
